import { formatSig } from './energyCalorimetry';
import { FUELS, type Fuel } from './energyCalorimetrySenior';
import {
  CO2_MOLAR_MASS,
  co2GramsPerMJ,
  combustionEquation,
  findFuel,
  fuelAtoms,
  prettyChemistry,
} from './fuelsSustainability';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError } from './modelInputContract';

/**
 * Emissions Explore (CT-INTERACT): the learner picks a reviewed fuel and types the mass burned.
 * Every number comes from the reviewed FUELS data (NIST ΔcH, IUPAC molar masses) through
 * n = m ÷ M, the combustion mole ratio and m = n × M(CO₂). Complete combustion only.
 */
export interface FuelEmissionsSetup {
  fuelId: string;
  fuelKg: number;
}

export type FuelEmissionsInput = 'fuelKg';

export const FUEL_EMISSIONS_FIELDS: ModelField<FuelEmissionsInput>[] = [
  { key: 'fuelKg', label: 'Mass of fuel burned', unit: 'kg', min: 0.001, max: 1000, step: 0.001 },
];

export const DEFAULT_FUEL_EMISSIONS_SETUP: FuelEmissionsSetup = { fuelId: 'octane', fuelKg: 1 };

const G_PER_KG = 1000;
const KJ_PER_MJ = 1000;

export interface FuelPerMJBar {
  fuelId: string;
  name: string;
  formula: string;
  gramsPerMJ: number;
}

export interface FuelEmissionsResult {
  fuel: Fuel;
  equation: string | null;
  fuelGrams: number;
  fuelMoles: number;
  co2Moles: number;
  co2Grams: number;
  co2Kg: number;
  energyKJ: number;
  energyMJ: number;
  /** Same value as co2GramsPerMJ(fuel): m(CO₂) ÷ energy for any mass. */
  co2GramsPerMJ: number;
  steps: WorkedStep[];
  /** Shown when the model reaches a limit, for example a fuel with no carbon. */
  limitNote: string | null;
}

const fmt = (value: number) => formatSig(value, 3);

/** Every reviewed fuel's CO₂ per MJ, for the comparison bars. Fuels the model cannot read are left out. */
export function perMJBars(): FuelPerMJBar[] {
  return FUELS.flatMap((fuel) => {
    const gramsPerMJ = co2GramsPerMJ(fuel);
    return gramsPerMJ === null ? [] : [{ fuelId: fuel.id, name: fuel.name, formula: fuel.formula, gramsPerMJ }];
  });
}

/** CO₂ from burning the learner's mass of fuel, with the worked steps. Throws on invalid input (fail closed). */
export function calculateFuelEmissions(setup: FuelEmissionsSetup): FuelEmissionsResult {
  const error = firstModelError(FUEL_EMISSIONS_FIELDS, { fuelKg: setup.fuelKg });
  if (error) throw new Error(error);
  const fuel = findFuel(setup.fuelId);
  const atoms = fuel ? fuelAtoms(fuel) : null;
  if (!fuel || !atoms || fuel.combustionkJPerMol >= 0) {
    throw new Error('Choose one of the reviewed fuels.');
  }
  const equation = combustionEquation(fuel);
  const fuelGrams = setup.fuelKg * G_PER_KG;
  const fuelMoles = fuelGrams / fuel.molarMass;
  const co2Moles = fuelMoles * atoms.carbon;
  const co2Grams = co2Moles * CO2_MOLAR_MASS;
  const co2Kg = co2Grams / G_PER_KG;
  const deltaH = Math.abs(fuel.combustionkJPerMol);
  const energyKJ = fuelMoles * deltaH;
  const energyMJ = energyKJ / KJ_PER_MJ;
  const gramsPerMJ = co2Grams / energyMJ;

  const steps: WorkedStep[] = [
    {
      id: 'ratio',
      title: 'Mole ratio from the balanced equation',
      formula: 'complete combustion',
      working: `${equation ? prettyChemistry(equation) : `${fuel.formula} + O₂ → products`}: 1 mol ${fuel.formula} gives ${atoms.carbon} mol CO₂`,
      status: 'done',
    },
    {
      id: 'fuel-moles',
      title: 'Amount of fuel',
      formula: 'n = m ÷ M',
      working: `n(${fuel.formula}) = ${fmt(fuelGrams)} g ÷ ${fuel.molarMass} g mol⁻¹ = ${fmt(fuelMoles)} mol`,
      status: 'done',
    },
    {
      id: 'co2-moles',
      title: 'Amount of CO₂',
      formula: 'n(CO₂) = carbon atoms × n(fuel)',
      working: `n(CO₂) = ${atoms.carbon} × ${fmt(fuelMoles)} mol = ${fmt(co2Moles)} mol`,
      status: 'done',
    },
    {
      id: 'co2-grams',
      title: 'Mass of CO₂ in grams',
      formula: 'm = n × M(CO₂)',
      working: `m(CO₂) = ${fmt(co2Moles)} mol × ${CO2_MOLAR_MASS} g mol⁻¹ = ${fmt(co2Grams)} g`,
      status: 'done',
    },
    {
      id: 'co2-kg',
      title: 'Mass of CO₂ in kilograms',
      formula: 'kg = g ÷ 1000',
      working: `m(CO₂) = ${fmt(co2Grams)} g ÷ ${G_PER_KG} g kg⁻¹ = ${fmt(co2Kg)} kg`,
      status: 'done',
    },
    {
      id: 'energy',
      title: 'Heat released',
      formula: 'Q = n × |ΔcH|',
      working: `Q = ${fmt(fuelMoles)} mol × ${deltaH} kJ mol⁻¹ = ${fmt(energyKJ)} kJ = ${fmt(energyMJ)} MJ`,
      status: 'done',
    },
    {
      id: 'per-mj',
      title: 'CO₂ for each MJ of heat',
      formula: 'm(CO₂) ÷ Q',
      working: `${fmt(co2Grams)} g ÷ ${fmt(energyMJ)} MJ = ${fmt(gramsPerMJ)} g CO₂ per MJ`,
      status: 'done',
      note: 'the same for any mass of this fuel',
    },
  ];

  return {
    fuel,
    equation,
    fuelGrams,
    fuelMoles,
    co2Moles,
    co2Grams,
    co2Kg,
    energyKJ,
    energyMJ,
    co2GramsPerMJ: gramsPerMJ,
    steps,
    limitNote:
      atoms.carbon === 0
        ? `${fuel.name} has no carbon atoms, so complete combustion makes only water. CO₂ released while the hydrogen was produced is outside this model.`
        : null,
  };
}

// Teaching goal (CT-INTERACT C7) ----------------------------------------------------------------

/** Methane's CO₂ per kg of fuel (2.74 kg) and per MJ (49.4 g): the reference for the last task. */
function methaneReference(): { kgPerKg: number; gramsPerMJ: number } {
  const methane = findFuel('methane');
  const atoms = methane ? fuelAtoms(methane) : null;
  const gramsPerMJ = methane ? co2GramsPerMJ(methane) : null;
  if (!methane || !atoms || gramsPerMJ === null) throw new Error('Methane is missing from the reviewed fuels.');
  return { kgPerKg: (atoms.carbon * CO2_MOLAR_MASS) / methane.molarMass, gramsPerMJ };
}

const METHANE_REFERENCE = methaneReference();

export const FUEL_EMISSIONS_QUESTION = 'Which fuel gives the most heat for the least CO₂?';

/** Teaching tasks (CT-INTERACT C7). Each check reads only the emissions result, never the UI. */
export const FUEL_EMISSIONS_CHALLENGES: ExploreChallenge<FuelEmissionsResult>[] = [
  {
    id: 'one-kg-co2',
    prompt: 'Burn just enough fuel to release 1 kg of CO₂ (between 0.95 and 1.05 kg).',
    hint: 'CO₂ mass is proportional to fuel mass, and 1 kg of octane gives about 3 kg of CO₂.',
    isMet: (result) => result.co2Kg >= 0.95 && result.co2Kg <= 1.05,
    why: 'About 0.32 kg of octane makes 1 kg of CO₂. The CO₂ is heavier than the fuel because each carbon atom joins two oxygen atoms from the air.',
  },
  {
    id: 'no-carbon',
    prompt: 'Find a fuel that releases heat but makes no CO₂ at all.',
    hint: 'Look at the formulas: which fuel has no C?',
    isMet: (result) => result.co2Grams === 0 && result.energyMJ > 0,
    why: 'Hydrogen contains no carbon, so 2H₂ + O₂ → 2H₂O makes only water. CO₂ released while the hydrogen was made is outside this model.',
  },
  {
    id: 'per-kg-trap',
    prompt: 'Find a fuel that makes less CO₂ per kg burned than methane, but more CO₂ for each MJ of heat.',
    hint: 'Burn 1 kg of each fuel, then compare its bar in the CO₂ per MJ chart with methane.',
    isMet: (result) =>
      result.co2Grams > 0 &&
      result.co2Grams / result.fuelGrams < METHANE_REFERENCE.kgPerKg &&
      result.co2GramsPerMJ > METHANE_REFERENCE.gramsPerMJ,
    why: 'Methanol makes only 1.37 kg of CO₂ per kg (methane 2.74 kg), but its oxygen atom adds mass and no heat, so it gives 60.6 g CO₂ per MJ against 49.4 g for methane. Compare fuels per MJ of heat, not per kg.',
  },
];
